/**
 * GWP claim layer — the ONE place the theme reads Kite's campaign and changes
 * which free gift is in the cart.
 *
 * Two surfaces need this and must never drift apart:
 *   • assets/quick-buy-gwp.js — our own picker in the mobile Add-to-Cart sheet
 *     (Kite doesn't render on collection pages, so we draw the UI ourselves).
 *   • assets/cart-gwp.js      — the skin over Kite's NATIVE widget in the cart
 *     drawer + PDP, which also drives the "swap the claimed gift" path Kite's
 *     own widget refuses to perform.
 *
 * WHAT KITE GIVES US
 *   gfg.settings.freeGifts[i]                       — one campaign
 *     .rulesList[j]                                 — { ruleType: 'SPEND_X', minimumCartValue: '60' }
 *       .freeGiftProduct[k]                         — { productId, handle, title, variants: [...] }
 *         .variants[0]                              — { variantId, variantTitle: '50ml', variantImage }
 *   gfg.gfgFreeGift.f.gfgAddFreeGiftToCart(line, i) — Kite's own claim call
 *
 * The gift variant is read from that config and NEVER guessed from the
 * product's variant list: the campaign gives away the 50ml, while the first
 * `\d+ml` variant in option order is the 5ml.
 *
 * HARD RULES (both learned the expensive way — see the notes at claim())
 *   1. Claiming goes through Kite's OWN function and nowhere else. We never
 *      POST the gift line ourselves.
 *   2. Every claim is gated on the current market (assets/gwp-market.js).
 *
 * Loaded from layout/theme.liquid ahead of every GWP consumer. Deferred
 * scripts run in document order, so this is defined before quick-buy-gwp.js
 * and before the cart drawer's cart-gwp.js.
 */
(function () {
  'use strict';

  // Shopper-facing wording from the theme's language file (snippets/js-strings.liquid).
  function str(key) {
    var s = window.notedStrings && window.notedStrings.gwp;
    return (s && s[key]) || '';
  }
  if (window.NotedGwpClaim) return;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /**
   * The campaign this storefront should be offering: the first enabled one with
   * a spend rule that has gift products, AND that the current market is
   * eligible for. Kite publishes its settings on EVERY market including the
   * ones the merchant excluded, so without the gate we advertise (and offer to
   * claim) a gift that Kite's claim fn refuses and the discount Function never
   * zeroes. See assets/gwp-market.js.
   *
   * @returns {{index: number, campaign: object, rule: object, ruleId: string,
   *            thresholdPence: number}|null}
   */
  function campaign() {
    var gs = window.gfg && window.gfg.settings && window.gfg.settings.freeGifts;
    if (!Array.isArray(gs)) return null;
    var gate = window.NotedGwpMarket;
    for (var i = 0; i < gs.length; i++) {
      var c = gs[i];
      if (!c || c.isEnabled === false) continue;
      if (gate && !gate.allows(c, i)) continue;
      var rules = c.rulesList || [];
      for (var j = 0; j < rules.length; j++) {
        var rule = rules[j];
        if (!rule || !rule.minimumCartValue) continue;
        if (!(rule.freeGiftProduct && rule.freeGiftProduct.length)) continue;
        return {
          index: i,
          campaign: c,
          rule: rule,
          // Kite's config carries no ruleId on this shop, so fall back to the
          // `{ruleType}_0` form its own cart lines use.
          ruleId: rule.ruleId || (rule.ruleType ? rule.ruleType + '_0' : 'SPEND_X_0'),
          thresholdPence: Math.round(parseFloat(rule.minimumCartValue || '0') * 100)
        };
      }
    }
    return null;
  }

  /**
   * Every gift the campaign offers, in Kite's own order.
   * @param {object} [camp] - a campaign() result; resolved if omitted
   * @returns {Array<{productId, variantId, title, sub, variant, image}>}
   */
  function gifts(camp) {
    camp = camp || campaign();
    if (!camp) return [];
    return (camp.rule.freeGiftProduct || []).map(function (p) {
      var v = (p.variants && p.variants[0]) || {};
      // Noted's Vendor field already carries the "Inspired By …" prefix, so only
      // add it when it's missing. (Kite's server-injected config has no vendor;
      // it appears once Kite hydrates shopifyData client-side.)
      var vendor = ((p.shopifyData && p.shopifyData.vendor) || '').trim();
      var sub = vendor ? (/^inspired\s+by\b/i.test(vendor) ? vendor : str('inspired_by').replace('{vendor}', vendor)) : '';
      var variantTitle = v.variantTitle || v.title || '';
      var img = v.variantImage
        || (p.shopifyData && p.shopifyData.featured_image)
        || (p.images && p.images[0] && p.images[0].originalSrc)
        || '';
      if (img.indexOf('//') === 0) img = 'https:' + img;
      return {
        productId: String(p.productId || ''),
        variantId: String(v.variantId || ''),
        title: p.title || '',
        sub: sub,
        variant: variantTitle && variantTitle !== 'Default Title' ? variantTitle : '',
        image: img
      };
    }).filter(function (o) { return o.variantId; });
  }

  /**
   * One gift by PRODUCT id — Kite's rendered rows only expose `product-id`.
   * @returns {object|null}
   */
  function giftForProduct(productId) {
    if (productId == null) return null;
    var want = String(productId);
    var list = gifts();
    for (var i = 0; i < list.length; i++) if (list[i].productId === want) return list[i];
    return null;
  }

  /** Gift lines currently in the cart → { variantId: lineKey }. Kite tags them. */
  function claimedIn(cart) {
    var out = {};
    (cart.items || []).forEach(function (it) {
      if (it.properties && it.properties._free_product === 'true') out[String(it.variant_id)] = it.key;
    });
    return out;
  }

  /**
   * Claim a gift through Kite's OWN function and nowhere else.
   *
   * There used to be a fallback that POSTed the line itself with Kite's
   * properties, on the belief that the server-side Function would zero it
   * regardless. That is FALSE on any market the campaign excludes: verified on
   * Ireland the same line lands at full price (no discount allocations), i.e.
   * the customer is charged for something the UI called free. Never add a gift
   * line we cannot guarantee will be zeroed.
   *
   * @returns {Promise<boolean>} false when the claim was refused / unavailable
   */
  function claim(variantId, camp) {
    camp = camp || campaign();
    if (!variantId || !camp) return Promise.resolve(false);
    var gate = window.NotedGwpMarket;
    if (gate && !gate.allows(camp.campaign, camp.index)) return Promise.resolve(false);
    try {
      var fn = window.gfg && window.gfg.gfgFreeGift && window.gfg.gfgFreeGift.f
        && window.gfg.gfgFreeGift.f.gfgAddFreeGiftToCart;
      if (typeof fn === 'function') {
        return Promise.resolve(fn({ variantId: String(variantId), _rule_id: camp.ruleId, quantity: 1 }, camp.index))
          .then(function () { return true; });
      }
    } catch (e) {}
    return Promise.resolve(false);
  }

  function removeKeys(keys) {
    return keys.reduce(function (p, key) {
      return p.then(function () {
        return fetch(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      });
    }, Promise.resolve());
  }

  /** Remove the gift lines for these variant ids (leaves any other gift alone). */
  function removeGiftLines(variantIds) {
    var want = (variantIds || []).map(String);
    return getCart().then(function (cart) {
      var claimed = claimedIn(cart);
      return removeKeys(want.map(function (id) { return claimed[id]; }).filter(Boolean));
    });
  }

  /** Remove EVERY gift line in the cart. */
  function removeAllGiftLines() {
    return getCart().then(function (cart) {
      var claimed = claimedIn(cart);
      return removeKeys(Object.keys(claimed).map(function (id) { return claimed[id]; }));
    });
  }

  /**
   * Swap the claimed gift for another one: clear whatever gift is in the cart,
   * then claim this variant. Single-select campaign, so the clear is not
   * optional — Kite refuses to add a second gift while one is held, which is
   * exactly why its own widget appears to do nothing when you pick a different
   * row.
   *
   * Order matters: remove FIRST. Claiming first and removing after would leave
   * the cart briefly holding two gifts, and Kite would decline the second.
   *
   * @returns {Promise<boolean>} whether the new gift was actually claimed
   */
  function swapTo(variantId, camp) {
    camp = camp || campaign();
    if (!variantId || !camp) return Promise.resolve(false);
    return removeAllGiftLines().then(function () { return claim(variantId, camp); });
  }

  /**
   * Free gift test, no-gift group: never let a gift line stay in the basket.
   *
   * Kite's campaign has auto-add off (checked live 29 Oct), so with every picker
   * hidden for this group (its CSS) and every claim refused (the market gate in
   * assets/gwp-market.js) a gift can't normally get in. This is the backstop
   * for one that does (a basket built before the visitor was grouped, or any
   * route the theme doesn't control): as soon as the basket shows a free gift
   * line, it is taken out and the basket re-rendered, without opening it.
   * The basket draws free gift lines with [data-cart-line-gift]
   * (snippets/cart-line.liquid), so this needs no extra /cart.js read unless a
   * gift is actually there. Only runs for the no-gift group.
   */
  var enforcing = false;
  function enforceNoGift() {
    var gate = window.NotedGwpMarket;
    if (!gate || typeof gate.noGiftGroup !== 'function' || !gate.noGiftGroup()) return;
    if (enforcing || !document.querySelector('[data-cart-line-gift]')) return;
    enforcing = true;
    removeAllGiftLines()
      .then(function () {
        if (window.NotedCart && typeof window.NotedCart.renderSilently === 'function') window.NotedCart.renderSilently(null);
      })
      .catch(function (err) { console.error('[gwp-claim] no-gift clean-up failed', err); })
      .then(function () { enforcing = false; });
  }

  function watchNoGift() {
    var gate = window.NotedGwpMarket;
    if (!gate || typeof gate.noGiftGroup !== 'function' || !gate.noGiftGroup()) return;
    enforceNoGift();
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; enforceNoGift(); }, 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // After DOMContentLoaded every deferred script (cart.js included) has run.
  // The no-gift group is read from CSS, so wait for the page's styles too.
  if (window.notedTests && window.notedTests.gift && window.notedTests.gift !== 'off') {
    if (document.readyState === 'complete') watchNoGift();
    else window.addEventListener('load', watchNoGift);
  }

  window.NotedGwpClaim = {
    campaign: campaign,
    gifts: gifts,
    giftForProduct: giftForProduct,
    claimedIn: claimedIn,
    getCart: getCart,
    claim: claim,
    swapTo: swapTo,
    removeGiftLines: removeGiftLines,
    removeAllGiftLines: removeAllGiftLines
  };
})();
